// Engineer home dashboard (Session AFF). The first screen an engineer sees: a greeting, four
// at-a-glance count tiles, today's jobs as tappable cards, a collapsible look-ahead, and quiet links
// to the Fault Assistant and site history. It loads instantly from the offline cache so it works in
// a no-signal basement. The raw whole-job list moved to My Jobs; this screen is the day's overview.
import { StatusPill } from "../../components/StatusPill";
import { primaryStatePill, type StatePillSpec } from "../../lib/statusRegistry";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ComponentType, ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CalendarDays, ClipboardList, AlertCircle, MessageSquare, CheckCircle2, ChevronDown, ChevronUp } from "lucide-react";
import JobTypeBadge from "../../components/JobTypeBadge";
import { jobReferenceLabel } from "../../lib/jobReference";
import { useEngineerData } from "./EngineerDataContext";
import { dayLabelOf, expandWorkDays, jobLinkOf, viewKeyOf } from "../../lib/engineerWorkDays";
import { SignOutButton } from "./EngineerLayout";
import { useAuth } from "../../lib/auth";
import { hasOwnEngineerRecords } from "../../lib/pwaIdentity";
import type { JobView } from "../../lib/offline/types";
import { api } from "../../lib/api";
import type { VanStockItem } from "../../lib/api";
import { ENG_CARD, ENG_CARD_LINK, ENG_CARD_OVERDUE_STYLE, ENG_PILL, ENG_SECTION_HEADING, DANGER_INK } from "./engineerUi";
import {
  SKYLINE_WATERMARK_COLOUR,
  SKYLINE_WATERMARK_OPACITY,
  SKYLINE_WATERMARK_COLOUR_LIGHT,
  SKYLINE_WATERMARK_OPACITY_LIGHT,
  SKYLINE_WATERMARK_HEIGHT,
} from "../../lib/skyline";
import { useTheme } from "../../lib/theme";
import {
  greetingForDate,
  firstNameFromDisplayName,
  countJobsToday,
  countJobsThisWeek,
  countOverdue,
  openJobsOnDay,
  openJobsWithNoDate,
  openJobsOverdue,
  upcomingDays,
} from "../../lib/engineerDashboard";
import { InstallNudge } from "./InstallNudge";
import PushBanner, { PushStatusRow } from "./PushBanner";
import { SurveyPhotoNoticesBanner } from "./projectSurvey/SurveyPhotoNotices";
import { PopTagScanButton, PopTagScanPanel } from "./PopTagHomeScan";
import { useHomeTagScan } from "./usePopTagHomeScan";

function whenLabel(iso: string | null): string {
  if (!iso) return "Unscheduled";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Unscheduled";
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

function timeLabel(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

// What the job sheet's post-submit navigate carries in router state (Session APF, PWA audit finding
// 7): before this, submitting a sheet dropped the engineer straight back on Today with the job
// silently gone and nothing said about it. `online` is always present on a real navigate but
// defaults to true here regardless, since a submission is the ordinary (online) case and the more
// alarming "saved offline" wording should only ever show when the caller genuinely said so.
export interface SubmittedNotice {
  ref: string;
  site: string;
  online: boolean;
}

// Router state is whatever the caller passed -- never trusted by shape -- so this is the one place
// that decides what counts as a genuine submission notice. Exported and pure so it unit tests
// without rendering the page.
export function readSubmittedState(state: unknown): SubmittedNotice | null {
  if (state === null || typeof state !== "object") return null;
  const submitted = (state as Record<string, unknown>).submitted;
  if (submitted === null || typeof submitted !== "object") return null;
  const { ref, site, online } = submitted as Record<string, unknown>;
  if (typeof ref !== "string" || typeof site !== "string") return null;
  return { ref, site, online: typeof online === "boolean" ? online : true };
}

export function EngineerHome() {
  // Session BDM: dayJobs lays a multi-day job out on every day the engineer is on; a one-day job is itself.
  const { jobs: ownJobs, loading, unreadMessages } = useEngineerData();
  const jobs = useMemo(() => expandWorkDays(ownJobs), [ownJobs]);
  const { user } = useAuth();
  // The skyline watermark is the one thing on this screen whose colour cannot be a CSS token: it
  // is a masked fill, so the value is read in JS. useTheme re-renders on a toggle (Session AFK).
  const { isDark } = useTheme();
  const now = new Date();
  const location = useLocation();
  const navigate = useNavigate();

  const firstName = firstNameFromDisplayName(user?.displayName);
  const greeting = firstName ? `${greetingForDate(now)}, ${firstName}` : greetingForDate(now);
  const dateLabel = now.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const shortDate = now.toLocaleDateString("en-GB", { day: "numeric", month: "short" });

  const todayCount = countJobsToday(jobs, now);
  const weekCount = countJobsThisWeek(jobs, now);
  const overdueCount = countOverdue(jobs, now);
  const overdueJobs = openJobsOverdue(jobs, now);
  const todayJobs = openJobsOnDay(jobs, now);
  // Session APF, PWA audit finding 11: a repair, a condition report or a site inspection can be
  // raised with no scheduled date, so it never lands in Today (which keys off a real calendar day)
  // and was otherwise findable only by hunting through "Unscheduled" on My Jobs.
  const noDateJobs = openJobsWithNoDate(jobs);

  const [submittedNotice, setSubmittedNotice] = useState<SubmittedNotice | null>(null);
  // Session APG: the POP tag scan button. One scan for the whole screen, shared by the button in the
  // greeting row and the panel beneath it.
  const tagScan = useHomeTagScan();

  // Read the job sheet's submission notice out of router state once, then clear the state (replace
  // with state: null) so a refresh -- which replays the last history entry's state -- or coming back
  // to this screen later never replays the same banner. Deliberately runs once for the incoming
  // navigation only, exactly like QuotesPage.tsx's own created-quote banner: the empty dependency
  // array means nothing here re-runs itself, so this cannot loop.
  useEffect(() => {
    const notice = readSubmittedState(location.state);
    if (notice) {
      setSubmittedNotice(notice);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <InstallNudge />
      <PushBanner />
      <SurveyPhotoNoticesBanner />

      {/* Greeting, and the POP tag scan button to the right of it (Session APG, Director decision, 21
          September 2026). The button is ALWAYS here, not conditional on a job being open: a tag is
          programmed per lift, so scanning it names its own lift and the platform finds the job. Its
          answer (or its question, when more than one job is open) appears directly beneath the row. */}
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="break-words font-heading text-[22px] tracking-wide text-ele-strong">{greeting}</h1>
          <p className="mt-0.5 font-body text-[13px] text-ele-muted">{dateLabel}</p>
        </div>
        <PopTagScanButton scan={tagScan} />
      </div>
      <PopTagScanPanel scan={tagScan} />

      {/* Job sheet submission confirmation (Session APF, PWA audit finding 7). Captured from router
          state by readSubmittedState above; see the effect for how it is read once and cleared.
          Dismissible, and gone for good once dismissed or once the screen has already consumed its
          state -- a refresh never brings it back. */}
      {submittedNotice && (
        <div role="status" className="mb-4 rounded-xl border border-ele-green bg-ele-card p-4 shadow-[0_1px_3px_var(--ele-shadow)]">
          <h2 className="font-heading text-base text-ele-green">
            {submittedNotice.online ? "Job sheet submitted" : "Job sheet saved"}
          </h2>
          <p className="mt-1 text-sm text-ele-muted">
            {`${submittedNotice.ref}, ${submittedNotice.site}. ${
              submittedNotice.online
                ? "Closed. The office will check and release the report. Nothing goes to the customer until then."
                : "It will upload by itself when you are back online."
            }`}
          </p>
          <button
            type="button"
            onClick={() => setSubmittedNotice(null)}
            className="mt-2 flex min-h-11 items-center font-menu text-xs tracking-wide text-ele-muted hover:text-ele-strong"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Four count tiles.
          Session AIB (mobile audit L5): `grid-cols-4` had no responsive prefix, so on a 320px phone
          each tile was 66px and the label had 50px of inner width after the tile's p-2 -- "Jobs
          Today" at 11px is about 57-60px, so it wrapped to two lines and read ragged against its
          three single-line neighbours. Below 375px the tiles now go two by two, which gives every
          label a full line. At 375px and up NOTHING changes: the row is the same four across it has
          always been. `min-[375px]:` is an arbitrary variant because Tailwind's own scale starts at
          sm:640px and this repo declares no custom screens; the precedent is
          OfficeDashboard.tsx:626. */}
      <div className="grid grid-cols-2 gap-2 min-[375px]:grid-cols-4">
        <Tile to="/engineer/jobs" icon={CalendarDays} label="Jobs Today">
          <span style={{ color: "var(--ele-loler-ink)" }}>{todayCount}</span>
        </Tile>
        <Tile to="/engineer/diary" icon={ClipboardList} label="This Week">
          <span className="text-ele-strong">{weekCount}</span>
        </Tile>
        <Tile to="/engineer/jobs" icon={AlertCircle} label="Overdue">
          {overdueCount > 0 ? (
            <span style={{ color: DANGER_INK }}>{overdueCount}</span>
          ) : (
            <span className="flex items-center justify-center gap-1">
              <span style={{ color: "var(--ele-green-ink)" }}>0</span>
              <CheckCircle2 size={16} color="#63C41F" aria-hidden="true" />
            </span>
          )}
        </Tile>
        <Tile to="/engineer/messages" icon={MessageSquare} label="Messages">
          <span style={{ color: unreadMessages > 0 ? "var(--ele-pill-ink-purple)" : "var(--ele-text-muted)" }}>{unreadMessages}</span>
        </Tile>
      </div>

      {/* Overdue (Session BAS): the overdue jobs themselves, oldest first, above Today, so an
          overdue job is one tap from Home exactly as today's is (it used to be the Overdue tile, then
          a hunt through My Jobs). Same card; the booked day shows in red where the time would be. */}
      {overdueJobs.length > 0 && (
        <section className="mt-6" aria-labelledby="home-overdue">
          <h2 id="home-overdue" className="mb-2 font-heading text-lg font-bold tracking-wide" style={{ color: DANGER_INK }}>
            Overdue
          </h2>
          <ul className="space-y-2">
            {overdueJobs.map((job) => (
              <li key={viewKeyOf(job)}>
                <JobCard job={job} overdue />
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Today */}
      <section className="mt-6">
        <div className="mb-2 flex items-baseline gap-2">
          <h2 className={ENG_SECTION_HEADING}>Today</h2>
          <span className="font-body text-[13px] text-ele-muted">{shortDate}</span>
        </div>
        {todayJobs.length > 0 || noDateJobs.length > 0 ? (
          <>
            {todayJobs.length > 0 && (
              <ul className="space-y-2">
                {todayJobs.map((job) => (
                  <li key={viewKeyOf(job)}>
                    <JobCard job={job} />
                  </li>
                ))}
              </ul>
            )}
            {/* Session APF, PWA audit finding 11: undated open work, grouped below today's dated
                cards so it is still found on the screen an engineer opens first, rather than only
                via "Unscheduled" on My Jobs. Same JobCard, same tap-through. */}
            {noDateJobs.length > 0 && (
              <div className={todayJobs.length > 0 ? "mt-4" : ""}>
                <h3 className="mb-2 font-body text-[13px] text-ele-muted">No date set</h3>
                <ul className="space-y-2">
                  {noDateJobs.map((job) => (
                    <li key={viewKeyOf(job)}>
                      <JobCard job={job} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Nothing dated today: say so plainly rather than leaving the undated group looking
                like the whole of today's work with no comment either way. */}
            {todayJobs.length === 0 && <p className="mt-3 text-sm text-ele-muted">Nothing else is scheduled for today.</p>}
          </>
        ) : loading ? null : (
          <p className={`${ENG_CARD} p-4 text-center text-ele-muted`}>
            No jobs scheduled today
          </p>
        )}
      </section>

      {/* View all jobs */}
      <Link
        to="/engineer/jobs"
        className="mt-3 flex min-h-11 items-center font-menu text-xs tracking-wide"
        style={{ color: "var(--ele-loler-ink)" }}
      >
        View all jobs
      </Link>

      {/* Coming up */}
      <ComingUp jobs={jobs} now={now} />

      {/* Quiet links: Fault Assistant, Site history, and the POP Tag scan where the phone supports it */}
      <section className="mt-6 space-y-3">
        {/* Fault Assistant (Session AFC): the general entry point (no lift context). The call-out job
            sheet and the lift record carry their own context-aware links. */}
        <QuietLink
          to="/engineer/fault-assistant"
          accent="#7d21c7"
          title="Fault Assistant"
          subtitle="Ask about a fault, drawing on manuals and this portfolio's history."
        />
        {/* Site and lift history (Session AEK, D33; since Session ASB, D-SEC-6, only the sites where
            the engineer holds open work): reached from the home screen rather than a nav tab. The
            subtitle says so, because an empty list between jobs is normal, not a fault. */}
        <QuietLink
          to="/engineer/history"
          accent="#2CB9F3"
          title="Site history"
          subtitle="Look up what a previous engineer found on the lifts at sites where you have work booked."
        />
        {/* My past visits (Session ASB, D-SEC-6): the engineer's OWN completed visits, which they keep
            for good, even after the site has left their site list. Engineer-row data, gated like Next
            of kin below (hasOwnEngineerRecords), so an office account with no engineer of its own is
            not offered a list that can only ever be empty. */}
        {hasOwnEngineerRecords(user) && (
          <QuietLink
            to="/engineer/history/mine"
            accent="#2CB9F3"
            title="My past visits"
            subtitle="Your own completed visits and their records, wherever they were."
          />
        )}
        {/* My expenses (Session ATE): a general claim, or reached from a job's own "Claim an expense
            for this job" link. Engineer-row data, gated the same way as My past visits above. */}
        {hasOwnEngineerRecords(user) && (
          <QuietLink
            to="/engineer/expenses"
            accent="#63C41F"
            title="My expenses"
            subtitle="Claim mileage, materials or another cost, and see what you have claimed."
          />
        )}
        {/* Report an accident or a near miss (Session AOM). A QUIET LINK rather than a sixth bottom
            tab, for the reason the POP Tag link below records: EngineerLayout's five tabs are a
            shipped nav design and adding to it is a nav redesign, not a feature. It sits here, above
            the POP Tag, because a near miss goes unreported when reporting it is awkward, and this is
            the screen an engineer is on between jobs. The accent is the danger token, which is what
            every other safety control on this platform uses. */}
        <QuietLink
          to="/engineer/report-accident"
          accent="var(--ele-danger)"
          title="Report an accident or near miss"
          subtitle="Get it on record now, before it is forgotten. The office takes it from there."
        />
        {/* Next of kin (Session AOU): Engineer-row data with no counterparty, gated the same way as
            the van stock section below (hasOwnEngineerRecords, not a role check) so the Director's
            own admin-linked Engineer row -- invisible on the office Engineers page -- can reach it
            here too. Hidden entirely for an oversight account with no Engineer row, matching Van
            stock's own "nothing useful behind this link" reasoning. */}
        {hasOwnEngineerRecords(user) && (
          <QuietLink
            to="/engineer/next-of-kin"
            accent="#2CB9F3"
            title="Next of kin"
            subtitle="Who the office can call if they cannot reach you on a missed check-in."
          />
        )}
        {/* Calendar feed (Session ATY): the engineer's own jobs and standby weeks in their own calendar app. Engineer-row
            data, gated exactly like Next of kin above. */}
        {hasOwnEngineerRecords(user) && (
          <QuietLink
            to="/engineer/calendar-feed"
            accent="#2CB9F3"
            title="Add my jobs to my calendar"
            subtitle="See your jobs and standby weeks in Google, Apple or Outlook calendar."
          />
        )}
        {/* The Guv POP Tag scan used to have a quiet link here (Session AND). Session APG replaced it
            with the round scan button in the greeting row at the top of this screen, which does the
            same job and finds the engineer's job as well. Keeping both would have been two ways onto
            one tap with two different results (one linked to a job, one to nothing), which is the
            two-flows situation the Director ruled out. The standalone /engineer/pop-tag screen is
            untouched and still reachable by its URL. */}
      </section>

      <VanStock />

      {/* Sign out (relocated from the shell header, Session AFF): quiet, centred. */}
      <div className="mt-8 flex flex-col items-center gap-2">
        <PushStatusRow />
        <SignOutButton className="flex min-h-11 items-center rounded-ele border border-ele-card-border px-4 font-menu text-xs tracking-wide text-ele-muted hover:text-ele-strong" />
      </div>

      {/* Skyline footer -- the HOME screen only (no other engineer screen renders this). Session AFG
          established the CSS MASK over the transparent purple line art, so the fill colour is exact
          by construction (the AFF filter chain read as washed-out purple; a filter can only
          approximate the hue). The mask's alpha keeps the anti-aliased skyline edges. It is a masked
          <div> because a mask fills the element's background, not an <img>'s pixels.
          Session AFJ SUPERSEDES AFG's ELE blue #2CB9F3 at 0.2, which measured a contrast of 1.28
          against the #050816 background and read as barely there: the watermark colour and opacity
          now come from the one shared source alongside the Client Portal footer.
          Session AFK: the engineer shell is themable, and a near-white fill vanishes on a light
          ground, so the pair flips to the measured dark-ink values on light. Both pairs live in
          lib/skyline.ts with the measurements that chose them. */}
      <div
        aria-hidden="true"
        data-ele-skyline=""
        className="mt-8 w-full"
        style={{
          height: SKYLINE_WATERMARK_HEIGHT,
          backgroundColor: isDark ? SKYLINE_WATERMARK_COLOUR : SKYLINE_WATERMARK_COLOUR_LIGHT,
          opacity: isDark ? SKYLINE_WATERMARK_OPACITY : SKYLINE_WATERMARK_OPACITY_LIGHT,
          WebkitMaskImage: "url('/brand/london-skyline.png')",
          maskImage: "url('/brand/london-skyline.png')",
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskPosition: "center",
          maskPosition: "center",
          WebkitMaskSize: "contain",
          maskSize: "contain",
        }}
      />
    </div>
  );
}

// A count tile: an icon, a big number (its colour supplied by the caller as children), a label. The
// whole tile taps through to the relevant list.
function Tile({
  to,
  icon: Icon,
  label,
  children,
}: {
  to: string;
  // className as well as color: the neutral icon tints are theme tokens now (Session AFK), and a
  // lucide stroke follows currentColor, so they are set by class rather than a literal.
  icon: ComponentType<{ size?: number; color?: string; className?: string }>;
  label: string;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      className={`${ENG_CARD} flex min-h-[84px] flex-col items-center justify-center gap-1 p-2 text-center transition-colors hover:bg-ele-row-hover`}
    >
      {/* No color prop: a lucide stroke is currentColor, so the muted token themes it. */}
      <Icon size={16} className="text-ele-muted" />
      <span className="font-heading text-[28px] font-bold leading-none">{children}</span>
      <span className="font-body text-[11px] leading-tight text-ele-muted">{label}</span>
    </Link>
  );
}

// A compact job card used in the Today list and the Coming up look-ahead. A call-out is amber with a
// warning icon; everything else is a blue spanner. The whole card opens the job sheet.
function JobCard({ job, overdue = false }: { job: JobView; overdue?: boolean }) {
  // An overdue card names the day it was booked for, not just the time: the time alone reads as today.
  const time = overdue && job.scheduledFor
    ? new Date(job.scheduledFor).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" })
    : timeLabel(job.scheduledFor);
  const inProgress = job.status === "in_progress";
  const holdPill = onHoldPillOf(job);
  return (
    <Link
      to={jobLinkOf(job)}
      className={`${ENG_CARD_LINK} items-center`}
      style={overdue ? ENG_CARD_OVERDUE_STYLE : undefined}
    >
      <div className="min-w-0 flex-1">
        <div className="mb-1.5">
          <JobTypeBadge type={job.type} equipmentAsset={job.equipmentAsset === true} />
        </div>
        <div className="truncate font-heading text-base font-bold leading-tight text-ele-strong">{job.siteName || "Site to be confirmed"}</div>
        {dayLabelOf(job) && <div className="text-xs font-semibold text-ele-body">{dayLabelOf(job)}</div>}
        {job.eleNumber && (
          <div className="truncate text-[13px]" title={jobReferenceLabel(job)} style={{ color: "var(--ele-loler-ink)" }}>
            {jobReferenceLabel(job)}
          </div>
        )}
      </div>
      <div className="flex flex-none flex-col items-end gap-1.5 text-right">
        {time && (
          <div className="font-heading text-[15px] font-semibold text-ele-strong" style={overdue ? { color: DANGER_INK } : undefined}>
            {time}
          </div>
        )}
        {holdPill ? (
          <StatusPill state={holdPill} />
        ) : (
          <span
            className={ENG_PILL}
            style={
              inProgress
                ? { backgroundColor: "var(--ele-pill-tint-loler)", color: "var(--ele-loler-ink)" }
                : { backgroundColor: "var(--ele-inset)", color: "var(--ele-text-muted)" }
            }
          >
            {inProgress ? "In progress" : "Scheduled"}
          </span>
        )}
      </div>
    </Link>
  );
}

// The next three days' open jobs, grouped by date, collapsed by default. Hidden entirely when there
// is nothing coming up (a tidy home is the point).
function ComingUp({ jobs, now }: { jobs: JobView[]; now: Date }) {
  const [open, setOpen] = useState(false);
  const days = upcomingDays(jobs, now, 3);
  const total = days.reduce((n, d) => n + d.jobs.length, 0);
  if (total === 0) return null;

  return (
    <section className="mt-6">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={`${ENG_CARD} flex min-h-11 w-full items-center justify-between px-4 py-2 transition-colors hover:bg-ele-row-hover`}
      >
        <span className={ENG_SECTION_HEADING}>
          Coming up <span className="text-ele-muted">({total})</span>
        </span>
        {open ? (
          <ChevronUp size={18} className="text-ele-muted" aria-hidden="true" />
        ) : (
          <ChevronDown size={18} className="text-ele-muted" aria-hidden="true" />
        )}
      </button>
      {open && (
        <div className="mt-2 space-y-4">
          {days.map((group) => (
            <div key={group.date.toISOString()}>
              <h3 className="mb-1 font-menu text-xs uppercase tracking-wide text-ele-muted">
                {group.date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
              </h3>
              <ul className="space-y-2">
                {group.jobs.map((job) => (
                  <li key={viewKeyOf(job)}>
                    <JobCard job={job} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// A quiet full-width link card (Fault Assistant, Site history): the card idiom, a coloured hover
// border + chevron, no left stripe.
function QuietLink({ to, accent, title, subtitle }: { to: string; accent: string; title: string; subtitle: string }) {
  return (
    <Link
      to={to}
      className={`${ENG_CARD} flex min-h-11 items-center justify-between gap-3 p-4 transition-colors hover:bg-ele-row-hover`}
    >
      <div className="min-w-0">
        <div className="font-heading text-lg text-ele-strong">{title}</div>
        <div className="mt-0.5 text-sm text-ele-muted">{subtitle}</div>
      </div>
      <span aria-hidden="true" className="flex-none font-heading text-2xl" style={{ color: accent }}>
        &rsaquo;
      </span>
    </Link>
  );
}

// A single job as a full-width list row (the old home list). Exported so My Jobs can render the same
// rows while its dedicated screen is built out.
export function JobRow({ job }: { job: JobView }) {
  const done = job.pendingSync || job.status === "complete";
  // Session ACR: an equipment job is titled "[catalogue name] | ELE-NNN-ENN" (the pipe per the
  // house separator rule) and labelled "Equipment service" in the same locked service colour.
  const isEquipment = job.equipmentAsset === true;
  const title = isEquipment
    ? [job.liftType || job.assetType, job.eleNumber].filter(Boolean).join(" | ")
    : jobReferenceLabel(job);
  return (
    <Link
      to={jobLinkOf(job)}
      className={`block rounded-ele border border-ele-purple-accent bg-ele-surface p-4 transition hover:border-ele-green ${
        done ? "opacity-80" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-heading text-lg text-ele-strong">{title}</div>
          {dayLabelOf(job) && <div className="text-xs font-semibold text-ele-body">{dayLabelOf(job)}</div>}
          <div className="text-sm text-ele-body">{job.siteName || "Site to be confirmed"}</div>
          {job.siteAddress && <div className="text-xs text-ele-body opacity-70">{job.siteAddress}</div>}
        </div>
        <div className="text-right">
          <JobTypeBadge type={job.type} equipmentAsset={isEquipment} />
          <div className="mt-1 text-xs text-ele-body">{whenLabel(job.scheduledFor)}</div>
          {/* Session SC9A: the one state this list shows besides the sync notes below, in the same look as the office. */}
          {onHoldPillOf(job) && <StatusPill state={onHoldPillOf(job)!} className="mt-1" />}
        </div>
      </div>
      {job.syncEscalated ? (
        // Session AEK (D32): this sheet has stopped retrying, so saying it will retry would be a
        // lie. Amber, and the same words as the badge and the job page.
        <div className="mt-3 font-menu text-xs uppercase tracking-wider text-amber-400">Needs the office</div>
      ) : job.pendingSync ? (
        <div className="mt-3 font-menu text-xs uppercase tracking-wider text-ele-blue">
          {job.syncError ? "Submitted, will retry sync" : "Submitted, waiting to sync"}
        </div>
      ) : job.status === "complete" ? (
        <div className="mt-3 font-menu text-xs uppercase tracking-wider text-ele-green">Submitted</div>
      ) : null}
    </Link>
  );
}

// The engineer's own van stock. Online-only (the job loop is the offline-first part);
// when offline it shows the last loaded list read-only. Marking an item used / adding
// one back adjusts the quantity on the server; items at/below threshold show amber.
function VanStock() {
  const { online } = useEngineerData();
  const { user } = useAuth();
  // Van stock belongs to an engineer's own van, and the route resolves it from the caller's
  // Engineer row. An oversight account has none, so this asked for a van that does not exist and
  // rendered the refusal as an error on the home screen -- on the Director's live session it 404'd
  // seven times in three minutes (Session AGV). It now simply does not appear.
  const ownRecords = hasOwnEngineerRecords(user);
  const [items, setItems] = useState<VanStockItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try {
      setItems(await api.engineers.myStock());
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (online && ownRecords) void load();
  }, [online, load, ownRecords]);

  if (!ownRecords) return null;

  async function adjust(partCode: string, delta: number) {
    try {
      const updated = await api.engineers.updateMyStock(partCode, { delta });
      setItems((xs) => xs.map((x) => (x.partCode === partCode ? updated : x)));
      setError(false);
    } catch {
      setError(true);
    }
  }

  // Nothing to show until we have loaded at least once with items (keep the home tidy).
  if (loaded && items.length === 0 && !error) return null;

  return (
    <section className="mt-8">
      <h2 className="mb-3 font-heading text-lg text-ele-blue">Your van stock</h2>
      {!online && items.length === 0 ? (
        <p className="text-sm text-ele-body opacity-70">Your van stock will load when you are back online.</p>
      ) : error && items.length === 0 ? (
        <p className="text-sm text-ele-body opacity-70">Could not load your van stock just now.</p>
      ) : (
        <>
          {/* An adjust that failed while the list is loaded: surface the error but keep the items on
              screen so the engineer can see the stock and try again (B3). */}
          {error && (
            <p role="alert" className="mb-2 text-sm text-ele-danger">
              Could not save that stock change. Check your connection and try again.
            </p>
          )}
          <ul className="space-y-2">
            {items.map((it) => (
              <li
                key={it.id}
                className={`flex items-center justify-between gap-3 rounded-ele border bg-ele-surface p-3 ${
                  it.needsReorder ? "border-amber-400" : "border-ele-purple-accent"
                }`}
              >
                <div>
                  <div className="text-ele-strong">{it.partCode}</div>
                  <div className="text-xs text-ele-body opacity-70">{it.description}</div>
                  {it.needsReorder && (
                    <div className="mt-0.5 font-menu text-[10px] uppercase tracking-wide text-amber-400">Reorder due</div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => adjust(it.partCode, -1)}
                    disabled={!online}
                    className="min-h-11 min-w-11 rounded-ele border border-ele-field-border px-3 py-1 text-lg leading-none text-ele-body disabled:opacity-40"
                    aria-label={`Use one ${it.partCode}`}
                  >
                    -
                  </button>
                  <span className="min-w-6 text-center font-heading text-lg text-ele-strong">{it.quantity}</span>
                  <button
                    type="button"
                    onClick={() => adjust(it.partCode, 1)}
                    disabled={!online}
                    className="min-h-11 min-w-11 rounded-ele border border-ele-field-border px-3 py-1 text-lg leading-none text-ele-body disabled:opacity-40"
                    aria-label={`Add one ${it.partCode}`}
                  >
                    +
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
      {!online && items.length > 0 && (
        <p className="mt-2 text-xs text-ele-body opacity-70">You are offline. Stock changes save when you are back online.</p>
      )}
    </section>
  );
}

/** Session SC9A: the phone shows only On hold (and Follow-on works on past visits), in the same look as the office. Null when the job is not on hold. */
export function onHoldPillOf(job: Pick<JobView, "status" | "holdReason" | "holdReasonText">): StatePillSpec | null {
  if (job.status !== "parts_pending") return null;
  return primaryStatePill({ status: job.status, holdReason: job.holdReason, holdReasonText: job.holdReasonText });
}
